import { useEffect, useRef } from "react";
import type { TableState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa, isRed, suitName, suitSymbol, suits } from "./cards";
import { unitName } from "./watching";
import { HokmScene } from "../hokm/scene";
import { HokmOnline } from "../hokm/controller";
import { Overlay } from "../ui/Overlay";
import { CupAfter, useCupNext } from "../ui/CupBits";
import { ResultAd } from "../ui/Ads";

/**
 * میزِ حکم — ظرفِ ری‌اکتیِ صحنه‌ی امری.
 *
 * صحنه (HokmScene) و کنترلر (HokmOnline) پورتِ یونیتی‌اند و DOM را خودشان
 * می‌کشند؛ این کامپوننت فقط ظرف را می‌دهد و کرومِ دورِ میز را: پنجره‌ی انتخابِ
 * حکم، نوارِ مهلت، و پنجره‌های پایانِ دست و مسابقه. وضعیتِ سرور مستقیم به این
 * کامپوننت نمی‌رسد — از صفِ کنترلر می‌گذرد، تا انیمیشنِ قبلی تمام شده باشد.
 */
export function HokmTable({ state }: { state: TableState }) {
  const host = useRef<HTMLDivElement>(null);
  const ctrl = useRef<HokmOnline | null>(null);
  const [ctrlSig] = useCtrlSignal();

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let c: HokmOnline | null = null;
    const scene = new HokmScene(el, state.seats.length || 4, {
      onCard: (i) => c?.clickCard(i),
      seatName: (l) => c?.name(l) ?? "حریف",
    });
    c = new HokmOnline(scene);
    ctrl.current = c;
    ctrlSig.value = c;
    c.start();
    const ro = new ResizeObserver(() => scene.relayout());
    ro.observe(el);
    return () => { ro.disconnect(); c?.dispose(); scene.destroy(); ctrl.current = null; ctrlSig.value = null; };
    // صحنه یک بار ساخته می‌شود؛ وضعیت‌های بعدی از صفِ کنترلر می‌آیند.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="table tv-host" ref={host}>
      <Chrome ctrl={ctrlSig} me={state.me} />
    </div>
  );
}

import { signal, type Signal } from "../net/signal";
import { useDealSheet } from "./DealProof";
import { nextLabel } from "./nextLabel";
import { BreakNext } from "./BreakNext";
import { botBare } from "./seats";
import { BotIcon } from "../ui/BotIcon";
function useCtrlSignal(): [Signal<HokmOnline | null>] {
  const ref = useRef<Signal<HokmOnline | null> | null>(null);
  if (!ref.current) ref.current = signal<HokmOnline | null>(null);
  return [ref.current];
}

/** کرومِ ری‌اکتیِ روی صحنه: پنجره‌ها و نوارِ مهلت. */
function Chrome({ ctrl, me }: { ctrl: Signal<HokmOnline | null>; me: number }) {
  const c = useSignal(ctrl);
  if (!c) return null;
  return <ChromeInner c={c} me={me} />;
}

function ChromeInner({ c, me }: { c: HokmOnline; me: number }) {
  const awaitingTrump = useSignal(c.awaitingTrump);
  const overlay = useSignal(c.overlay);
  const proof = useSignal(c.proof);
  const deal = useDealSheet((l) => c.name(l));
  const st = c.state;
  const cupNext = useCupNext() && !st.watching;

  return (
    <>
      {/* کارتِ کوچکِ وسطِ میز، نه پرده‌ی تمام‌صفحه — کاربر، ۲۲ اکتبر ۲۰۲۶: «کارت‌های خودم را نمی‌بینم که
          حکم کنم». پرده‌ی تیره و تارِ پیشین همان پنج برگی را می‌پوشاند که حکم از رویشان خوانده می‌شود؛ حالا
          دست و حریف‌ها پیدا می‌مانند (style.css، .hktrump). */}
      {awaitingTrump && !st.matchOver && (
        <div className="shtrump hktrump" role="group" aria-label="حکم را بخوان">
          <div className="head">حکم را بخوان</div>
          <div className="suits">
            {suits.map((s) => (
              <button key={s} className={`suitbtn ${isRed(s) ? "red" : ""}`} onClick={() => c.pickTrump(s)} aria-label={suitName[s]}>
                {suitSymbol[s]}
              </button>
            ))}
          </div>
          <div className="muted">از روی پنج برگ پایین بخوان؛ بقیه بعد از خواندن حکم پخش می‌شود.</div>
        </div>
      )}

      {overlay?.kind === "hand" && (
        <Overlay label="پایان دست" className="minor" card={st.watching ? "even" : overlay.unit === st.myUnit ? "win" : "lose"}>
          <div className="vt">
            {st.watching
              ? `این دست را ${unitName(overlay.unit, st.teams)} برد`
              : overlay.unit === st.myUnit ? "این دست را بردیم" : "این دست را حریف برد"}
          </div>
          <div className="muted">{overlay.kot ? (overlay.hakemKot ? "کُت حاکم — " : "کُت — ") : ""}{fa(overlay.points)} امتیاز</div>
          <BreakNext watch={c.brk} label={nextLabel("دست", st.watching ? "even" : overlay.unit === st.myUnit ? "win" : "lose")} onNext={() => c.nextHand()} />
          {deal.chip(proof)}
        </Overlay>
      )}

      {overlay?.kind === "match" && (
        <Overlay label="پایان مسابقه" card={st.watching ? "even" : overlay.unit === st.myUnit ? "win" : "lose"}
                 after={<ResultAd won={st.watching ? null : overlay.unit === st.myUnit} watching={st.watching} />}>
          <div className="vt">
            {st.watching
              ? `${unitName(overlay.unit, st.teams)} مسابقه را برد`
              : overlay.unit === st.myUnit ? "مسابقه را بردی!" : "مسابقه را باختی"}
          </div>
          <div className="muted vscore">{st.score.map(fa).join(" - ")}</div>
          {!st.watching && <CupAfter />}
          <button className={cupNext ? "btn ghost" : "btn"} onClick={() => client.exitTable()}>
            {st.watching ? "پایان تماشا" : "برگرد به خانه"}
          </button>
        </Overlay>
      )}
      <span hidden>{me}</span>
      {deal.sheet}
    </>
  );
}

/**
 * نوارِ بالای میزِ حکم: دست‌های برده و امتیازِ مسابقه.
 *
 * خالِ حکم این‌جا نیست: مدالِ درشتِ حکم روی خودِ میز است (HokmScene، همان BuildCenter ِ اپ) و با
 * اعلام از وسط به جایش می‌آید. نسخه‌ی کوچکش این‌جا همان خال را دوبار نشان می‌داد
 * (کاربر، ۲۰ اکتبر ۲۰۲۶: «دوجا خالِ حکم را نشان می‌دهیم که تکراری است»).
 */
export function HokmScorebar({ state }: { state: TableState }) {
  const units = state.tricksWon.length;
  // هر دو از سرور می‌آیند؛ قاعده این‌جا تکرار نمی‌شود.
  const pips = state.tricksToWin || 7;
  const mine = state.seatUnit[state.me] ?? 0;
  const teams = units === 2 && state.seats.length > units;
  const rows = units === 2 ? [mine, 1 - mine] : [mine, ...Array.from({ length: units }, (_, u) => u).filter((u) => u !== mine)];

  return (
    <div className="scorebar">
      <div className="rows">
        {rows.map((u, r) => (
          <div key={u} className={`srow ${r === 0 ? "us" : "them"}`}>
            <span className="who">{
              // تماشاچی «ما» ندارد: هر دو ردیف نامِ تیمِ خودش را می‌گیرد.
              state.watching ? unitName(u, teams)
                : r === 0 ? (teams ? "ما" : "من")
                : (teams ? "حریف" : <>{state.seats[u]?.bot && <BotIcon size={12} />}{botBare(state.seats[u]?.name || "حریف", state.seats[u]?.bot)}</>)
            }</span>
            <span className="pips">
              {Array.from({ length: pips }, (_, i) => {
                const on = i < (state.tricksWon[u] ?? 0);
                // کلید حالت را در خود دارد، پس نقطه‌ای که روشن می‌شود از نو
                // سوار می‌شود و انیمیشنِ تکان یک‌بار اجرا می‌شود.
                return <i key={`${i}-${on}`} className={on ? "on" : ""} />;
              })}
            </span>
            <b className="num">{fa(state.tricksWon[u] ?? 0)}</b>
          </div>
        ))}
      </div>

      <div className="match">
        <span className="l">بازی</span>
        <b>{state.score.map(fa).join(" - ")}</b>
        <span className="l">از {fa(state.matchTarget)}</span>
      </div>
    </div>
  );
}
